import * as React from 'react';

import { Dispatch } from 'redux';
import { connect, useSelector } from 'react-redux';
import AppState from '@webapp/store/state';
import { push, goBack, CallHistoryMethodAction } from 'connected-react-router';

import styled, { css } from 'styled-components';
import { getWidth } from '@components/styles';

import PricingCtrl from '@webapp/controllers/pricing';
import MotionOpacity from '@components/motion/motion-opacity';

import { Price } from '@common/types/price/price';
import { Product } from '@services/product/types/product';
import { CreateOrder, EmptyBasket } from '../../store/orders/action-creators';
import { Subscription } from '@services/subscription/types/subscription';

import { getSubscriptionChange } from '../../store/subscription/selectors';
import { PlanNames } from '@services/product/types/plan-names';
import { PaymentFrequency } from '@services/product/types/payment-frequency';

import { isEntryLevelPlan } from '../../utils/subsciptions/is-entry-level-plan';
import { Order } from '../../types/orders/order';
import { CustomerMetadata } from '@services/payment/types/api/customer-metadata';
import {
  CancelSubscription,
  CreateSubscription,
} from '../../store/subscription/action-creators';
import SimpleArtist from '../../../../../common/types/artist/simple-artist';

const maxArtistFree = 1;

interface PricingPageProps {
  className?: string;
}

interface StateProps {
  goToPayment: () => CallHistoryMethodAction;
  selectPlan: (product: Product, price: Price) => void;
  goBack: () => CallHistoryMethodAction;
  emptyBasket: () => EmptyBasket;
  createSubscription: (
    currentSubscription: undefined | Subscription,
    order: Order,
    customer: CustomerMetadata
  ) => CreateSubscription;
  cancelSubscription: () => CancelSubscription;
  hasPlan: boolean;
  clientId: string;
  currentSubscription?: Subscription;
  artist?: SimpleArtist;
  goToDowngradePlan: () => CallHistoryMethodAction;
  artistsLength: number;
}

const mapStateToProps = (state: AppState, props: any) => {
  const { clientId, id } = props.match?.params;

  const foundSubscription = state.subscriptionManager.summary?.artists.find(
    (s) => s.id === clientId
  );

  const foundArtist = state.artist.myArtists.find((s) => s.id === clientId);

  const artist: SimpleArtist | undefined = foundArtist
    ? ({
        id: clientId,
        name: foundArtist.name,
        avatar: foundArtist.avatar,
      } as SimpleArtist)
    : undefined;

  const subscription = foundSubscription?.subscriptions.find(
    (s) => s.id === id
  )?.subscription;

  return {
    hasPlan: subscription?.isActive,
    clientId,
    currentSubscription: subscription,
    artist,
    artistsLength: state.artist.myArtists?.length || 0,
  };
};

const mapDispatchToProps = (dispatch: Dispatch, props: any) => {
  const { clientId, id } = props.match?.params;

  return {
    goToPayment: () => dispatch(push(`/payment/${clientId}/${id || ''}`)),
    goBack: () => dispatch(goBack()),
    emptyBasket: () => dispatch(new EmptyBasket()),
    selectPlan: (product: Product, price: Price) =>
      dispatch(
        new CreateOrder({
          product,
          price,
          quantity: 1,
        })
      ),
    goToDowngradePlan: () =>
      dispatch(push(`/plans/${clientId}/${id}/downgrade`)),

    cancelSubscription: () => dispatch(new CancelSubscription(clientId)),
    createSubscription: (
      currentSubscription: undefined | Subscription,
      order: Order,
      customer: CustomerMetadata
    ) =>
      dispatch(
        new CreateSubscription(
          undefined,
          undefined,
          clientId,
          order,
          customer,
          'artist',
          currentSubscription,
          undefined,
          undefined,
          undefined,
          undefined,
          'plan upgrade'
        )
      ),
  };
};

export const PricingPage = (props: PricingPageProps & StateProps) => {
  const { emptyBasket } = props;
  const isUnsubscribing = useSelector(
    (state: AppState) => state.router.location.state?.['isUnsubscribing']
  );
  React.useEffect(() => {
    emptyBasket();
  }, [emptyBasket]);

  const handleSelection = (product: Product, price: Price) => {
    if (props.artist) {
      props.selectPlan(product, price);

      const change = getSubscriptionChange(
        props.currentSubscription,
        product.name as PlanNames,
        price.frequency as PaymentFrequency
      );

      if (change === 'DOWNGRADE') {
        return props.goToDowngradePlan();
      }

      if (isEntryLevelPlan(product)) {
        const order: Order = {
          product,
          price,
          quantity: 1,
        };
        if (props.currentSubscription) {
          return props.cancelSubscription();
        }

        return props.createSubscription(undefined, order, props.artist);
      }

      return props.goToPayment();
    }
  };

  if (!props.clientId) {
    return null;
  }

  return (
    <MotionOpacity className={props.className}>
      <PricingContainer>
        <PricingCtrl
          hideFrequency={true}
          onSelection={handleSelection}
          currentSubscription={props.currentSubscription}
          goBack={props.goBack}
          hideFree={
            isUnsubscribing ? false : props.artistsLength > maxArtistFree
          }
        />
      </PricingContainer>
    </MotionOpacity>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(PricingPage)`
  position: relative;
  margin-top: 24px;
  min-height: calc(100vh - 100px);

  ${({ theme }) => `@media screen and ${theme.device.laptopS}`} {
    ${(props) =>
      props.hasPlan
        ? css`
            margin-top: 80px;
            min-height: calc(100vh - 100px - 80px);
          `
        : css`
            margin-top: 150px;
            min-height: calc(100vh - 100px - 150px);
          `};
    padding-bottom: 50px;
  }
`);

const PricingContainer = styled.div`
  margin: auto;
  max-width: ${getWidth(6)};

  ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
    max-width: ${getWidth(10)};
  }
`;
